/* Left pane of the route editor: the points a stop can be added from.
   Normative rules: docs/design/spec/.

   Server-searched (debounced) and PAGINATED — it loads the next page as you scroll
   (`points/?page=N`), so every point is reachable, not just the first page.

   A row is BOTH a drag source and a button. Dragging is the mouse gesture, but native HTML5 drag
   fires no events on a touch screen and cannot be started from the keyboard, so selecting a row
   arms it and the calendar's day cells then offer "Add here". Same action, two routes — see
   docs/design/spec/03-patterns.md#accessibility-floors. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Empty, Flex, Input, Skeleton, Typography } from "antd";
import { CloseOutlined, HolderOutlined, SearchOutlined } from "@ant-design/icons";

import { DROPDOWN_PAGE_SIZE, FILTER_DEBOUNCE_MS } from "../../../config/config";
import { ROUTE_CALENDAR } from "../../../config/lang";
import { call } from "../../../util/action";
import { translate } from "../../../util/util";

// The MIME type used to carry a dragged point between the panel and calendar cells.
export const POINT_DND_TYPE = "application/x-picsell-point";

// Only the fields the calendar and the save path need.
const payloadOf = point => ({ id: point.id, name: point.name, address: point.address, locality: point.locality });

export default function PointSearchPanel({ lang, armed, onArm }) {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef(1);
  const loadingRef = useRef(false); // synchronous guard so fast scroll can't double-fetch
  const debounceRef = useRef(null);

  const fetchPage = useCallback((pageNum, search, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const q = search ? `&search=${encodeURIComponent(search)}` : "";
    call({ method: "GET", url: `points/?page=${pageNum}&page_size=${DROPDOWN_PAGE_SIZE}${q}` }, resp => {
      const items = Array.isArray(resp) ? resp : resp?.results || [];
      const more = Array.isArray(resp) ? false : Boolean(resp?.next);
      pageRef.current = pageNum;
      setResults(prev => (append ? [...prev, ...items] : items));
      setHasMore(more);
      loadingRef.current = false;
      setLoading(false);
    });
  }, []);

  // Debounced search resets to the first page.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPage(1, term, false), FILTER_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [term, fetchPage]);

  const onScroll = event => {
    const el = event.currentTarget;
    if (hasMore && !loadingRef.current && el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      fetchPage(pageRef.current + 1, term, true);
    }
  };

  return (
    <Flex vertical className="rc-panel">
      <div className="rc-panel__search">
        <Input
          value={term}
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("search_placeholder")}
          aria-label={t("search_placeholder")}
          onChange={event => setTerm(event.target.value)}
        />
      </div>

      {/* The selection is state the rest of the editor depends on, and it changes without a
          navigation, so it announces itself (spec/03: live regions). */}
      <div className="rc-panel__armed" aria-live="polite">
        {armed ? (
          <Flex align="center" gap={8}>
            <Typography.Text className="rc-panel__armed-name" ellipsis={{ tooltip: armed.name }}>
              {armed.name}
            </Typography.Text>
            <Typography.Text type="secondary" className="rc-panel__armed-hint">
              {t("point_selected")}
            </Typography.Text>
            <Button type="text" size="small" icon={<CloseOutlined />} aria-label={t("clear_selection")} onClick={() => onArm(null)} />
          </Flex>
        ) : null}
      </div>

      <div className="rc-panel__list" onScroll={onScroll}>
        {!loading && results.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_points_found")} />
        ) : (
          results.map(point => {
            const selected = armed?.id === point.id;
            return (
              <button
                key={point.id}
                type="button"
                className={`rc-point${selected ? " rc-point--on" : ""}`}
                aria-pressed={selected}
                title={point.address || ""}
                draggable
                onDragStart={event => {
                  event.dataTransfer.setData(POINT_DND_TYPE, JSON.stringify(payloadOf(point)));
                  event.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => onArm(selected ? null : payloadOf(point))}>
                <HolderOutlined className="rc-point__grip" />
                <span className="rc-point__body">
                  <span className="rc-point__name">{point.name}</span>
                  {point.locality?.name || point.address ? <span className="rc-point__sub">{point.locality?.name || point.address}</span> : null}
                </span>
              </button>
            );
          })
        )}
        {loading ? <Skeleton active paragraph={{ rows: 3 }} title={false} className="rc-panel__sk" /> : null}
      </div>
    </Flex>
  );
}
